import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import TradeBalanceTreemap from '../components/TradeBalanceTreemap';
import ECIBumpChart from '../components/ECIBumpChart';
import StrategicSupplierDependenceCompatible from '../components/StrategicSupplierDependenceCompatible';
import DiversificationStrategicAnalysis from '../components/DiversificationStrategicAnalysis';
import SupplierConcentrationTreemap from '../components/SupplierConcentrationTreemap';
import WorldMap from '../components/WorldMap';
import { getCountrySituation, getCountrySituationAI } from '../services/countrySituationApi';
import { getCountryPolicyDetails, getPolicySummary } from '../services/policyDetailsApi';

const PartnersRiskPage = () => {
  const { isDarkMode } = useTheme();
  const [supplierDependenceMineral, setSupplierDependenceMineral] = useState('Lithium');
  const [availableSupplierMinerals] = useState(['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
  const [partnerData, setPartnerData] = useState([]);
  const [selectedMineral, setSelectedMineral] = useState('Lithium');
  const [selectedYear, setSelectedYear] = useState('2023');
  const [availableYears, setAvailableYears] = useState(['2024', '2023', '2022', '2021', '2020']);
  const [availableMinerals] = useState(['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite']);
  const [loading, setLoading] = useState(true);
  
  // Country detail panel states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryDetail, setCountryDetail] = useState(null);
  const [countrySituation, setCountrySituation] = useState(null);
  const [policyDetails, setPolicyDetails] = useState(null);
  const [policySummary, setPolicySummary] = useState(null);
  const [countrySituationAI, setCountrySituationAI] = useState(null);
  const [situationLoading, setSituationLoading] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  
  // Trade Trends KPIs states
  const [tradeTrendsKPIs, setTradeTrendsKPIs] = useState(null);
  const [tradeTrendsLoading, setTradeTrendsLoading] = useState(true);
  const [tradeTrendsError, setTradeTrendsError] = useState(null);
  
  // Map container ref for positioning
  const mapContainerRef = useRef(null);

  // Fetch partner data
  useEffect(() => {
    console.log(`🚀 useEffect triggered for mineral: ${selectedMineral}, year: ${selectedYear}`);
    const fetchPartnerData = async () => {
      try {
        setLoading(true);
        console.log(`🔄 Fetching comprehensive partner data for ${selectedMineral} in ${selectedYear}`);
        const response = await fetch(`/api/partner-risk/comprehensive?mineral=${selectedMineral}&year=${selectedYear}`);
        if (response.ok) {
          const responseData = await response.json();
          console.log(`📊 Received comprehensive data for ${selectedMineral} in ${selectedYear}:`, responseData);
          
          // Handle the nested data structure from comprehensive API
          const data = responseData.data || responseData;
          console.log(`📊 Extracted data object:`, data);
          
          if (data && data.suppliers && Array.isArray(data.suppliers) && data.suppliers.length > 0) {
            // Transform the data to match the expected format for WorldMap
            const transformedData = data.suppliers.map(country => ({
              name: country.country || country.partner_country_name,
              country: country.country || country.partner_country_name,
              partner_risk: country.partner_risk_score || 0, // This is avg_fsi_wgi from the database
              dependency_ratio: country.dependency_ratio || 0,
              trade_restrictions: 0, // Not available in partner risk data
              iso3: country.country_code || country.partner_country_code,
              wgi_score: country.avg_normalised_wgi || 0,
              fsi_score: country.normalised_fsi || 0, // This is now aliased from avg_fsi_wgi
              risk_level: country.risk_level || 'Unknown',
              import_percentage: country.import_percentage || 0,
              trade_volume: country.trade_volume || 0
            }));
            console.log(`✅ Transformed comprehensive data for ${selectedMineral} in ${selectedYear}:`, transformedData);
            setPartnerData(transformedData);
          } else {
            // No data available
            console.log(`⚠️ No supplier data available for ${selectedMineral} in ${selectedYear}. Data structure:`, data);
            setPartnerData([]);
          }
        } else {
          // API error - no data
          console.error(`❌ API error for ${selectedMineral} in ${selectedYear}:`, response.status);
          setPartnerData([]);
        }
      } catch (error) {
        console.error('Error fetching partner data:', error);
        // API error - no data
        setPartnerData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerData();
  }, [selectedMineral, selectedYear]);

  // Load trade trends KPIs
  useEffect(() => {
    const loadTradeTrendsKPIs = async () => {
      try {
        setTradeTrendsLoading(true);
        setTradeTrendsError(null);
        const response = await fetch('/api/trade-trends-kpis');
        const data = await response.json();
        if (data.success) {
          setTradeTrendsKPIs(data.data);
        } else {
          console.error('Error loading trade trends KPIs:', data.error);
          setTradeTrendsError(data.error || 'Failed to load import dependency insights');
        }
      } catch (error) {
        console.error('Error loading trade trends KPIs:', error);
        setTradeTrendsError('Failed to load import dependency insights. Please try again later.');
      } finally {
        setTradeTrendsLoading(false);
      }
    };
    loadTradeTrendsKPIs();
  }, []);

  const handleCountryClick = async (countryData, event) => {
    console.log('Country clicked:', countryData);
    
    // Store click coordinates for positioning the detail panel
    if (event) {
      // Use clientX and clientY for more accurate positioning relative to viewport
      countryData.clickX = event.clientX;
      countryData.clickY = event.clientY;
      
      // Also store the map container's bounding rect for better positioning
      if (mapContainerRef.current) {
        const mapRect = mapContainerRef.current.getBoundingClientRect();
        countryData.mapOffsetX = mapRect.left;
        countryData.mapOffsetY = mapRect.top;
        
        // Adjust click coordinates relative to the map container
        countryData.clickXRelative = event.clientX - mapRect.left;
        countryData.clickYRelative = event.clientY - mapRect.top;
      }
      
      // Debug: Log click coordinates
      console.log('Country clicked with positioning:', {
        country: countryData.country || countryData.name,
        clientX: event.clientX,
        clientY: event.clientY,
        clickX: countryData.clickX,
        clickY: countryData.clickY,
        clickXRelative: countryData.clickXRelative,
        clickYRelative: countryData.clickYRelative,
        mapOffsetX: countryData.mapOffsetX,
        mapOffsetY: countryData.mapOffsetY,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      });
    }
    
    setSelectedCountry(countryData);
    
    try {
      const countryName = countryData.country || countryData.name || 'Unknown';
      
      // Fetch country situation and policy details
      await Promise.all([
        fetchCountrySituation(countryName),
        fetchPolicyDetails(countryName)
      ]);
      
      // Create enhanced country detail with comprehensive information
      const detail = {
        countryInfo: {
          name: countryName,
          partnerRiskScore: countryData.partner_risk || 0, // This is avg_fsi_wgi from the database
          dependencyRatio: countryData.dependency_ratio || 0,
          wgiScore: countryData.wgi_score || 0, // avg_normalised_wgi
          fsiScore: countryData.fsi_score || 0, // normalised_fsi
          riskLevel: countryData.risk_level || 'Unknown',
          importPercentage: countryData.import_percentage || 0,
          tradeVolume: countryData.trade_volume || 0
        }
      };
      
      setCountryDetail(detail);
      
    } catch (error) {
      console.error('Error processing country click:', error);
      // Provide fallback data
      setCountryDetail({
        countryInfo: {
          name: countryData.country || countryData.name || 'Unknown',
          partnerRiskScore: countryData.partner_risk || 0, // This is avg_fsi_wgi from the database
          dependencyRatio: countryData.dependency_ratio || 0,
          wgiScore: countryData.wgi_score || 0, // avg_normalised_wgi
          fsiScore: countryData.fsi_score || 0, // normalised_fsi
          riskLevel: countryData.risk_level || 'Unknown',
          importPercentage: countryData.import_percentage || 0,
          tradeVolume: countryData.trade_volume || 0
        }
      });
    }
  };

  const fetchCountrySituation = async (countryName) => {
    if (!countryName) return;
    
    setSituationLoading(true);
    try {
      const [situation, aiResult] = await Promise.allSettled([
        getCountrySituation(countryName),
        getCountrySituationAI(countryName)
      ]);

      if (situation.status === 'fulfilled' && situation.value && !situation.value.error) {
        setCountrySituation(situation.value);
      } else {
        setCountrySituation(null);
      }

      if (aiResult.status === 'fulfilled' && aiResult.value && aiResult.value.success) {
        setCountrySituationAI(aiResult.value.data);
      } else {
        setCountrySituationAI(null);
      }
    } catch (error) {
      console.error('Error fetching country situation:', error);
      setCountrySituation(null);
    } finally {
      setSituationLoading(false);
    }
  };

  const fetchPolicyDetails = async (countryName) => {
    if (!countryName) return;
    
    setPolicyLoading(true);
    try {
      const [policies, summary] = await Promise.all([
        getCountryPolicyDetails(countryName, selectedMineral, parseInt(selectedYear)),
        getPolicySummary(countryName, selectedMineral, parseInt(selectedYear))
      ]);
      setPolicyDetails(policies);
      setPolicySummary(summary);
    } catch (error) {
      console.error('Error fetching policy details:', error);
      setPolicyDetails(null);
      setPolicySummary(null);
    } finally {
      setPolicyLoading(false);
    }
  };

  const closeCountryDetail = () => {
    setSelectedCountry(null);
    setCountryDetail(null);
    setCountrySituation(null);
    setPolicyDetails(null);
    setPolicySummary(null);
  };

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white w-full page-container transition-colors duration-200">
      <div className="w-full px-4 py-8">
        {/* Section 1: Partner Map (India's import partners) */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Import Partners</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This map displays the network of international trade partners involved in the supply of critical minerals. It provides insights into trade concentration, partner diversity, and opportunities for supply-chain expansion. </p>
            
            {/* Main Layout - Left: Selection Criteria, Right: Visualization */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Side - Selection Criteria */}
              <div className="lg:col-span-1">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 space-y-4 transition-colors duration-200">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Selection Criteria</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mineral</label>
                    <select
                      value={selectedMineral}
                      onChange={(e) => setSelectedMineral(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {availableMinerals.map(mineral => (
                        <option key={mineral} value={mineral}>{mineral}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Year</label>
                    <select
                      value={selectedYear}
                      onChange={(e) => {
                        console.log(`📅 Year changed from ${selectedYear} to ${e.target.value}`);
                        setSelectedYear(e.target.value);
                      }}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Right Side - Visualization Area */}
              <div className="lg:col-span-3">
                <div className="relative" ref={mapContainerRef}>
                  {/* World Map */}
                  <WorldMap
                    key={`${selectedMineral}-${selectedYear}-${partnerData.length}`}
                    data={partnerData}
                    selectedMetric="Partner Risk"
                    onCountryClick={handleCountryClick}
                    height={500}
                    className="w-full"
                    enableZoom={true}
                  />
                  
                  {/* Country Detail Panel - Theme-Aware Design */}
                  {selectedCountry && countryDetail && (
                    <div className="fixed bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-h-[500px] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-600 z-50 transition-colors duration-200"
                         style={{
                           ...(() => {
                             const modalWidth = 384; // w-96 = 24rem = 384px
                             const modalHeight = 500; // max-h-[500px]
                             const padding = 20;
                             const offset = 10;
                             
                             // Use viewport coordinates directly for fixed positioning
                             let left = selectedCountry.clickX || 200;
                             let top = selectedCountry.clickY || 200;
                             
                             // Position relative to the viewport (fixed positioning)
                             left = left + offset;
                             top = top - offset;
                             
                             // If modal would go off the right edge, position it to the left of click
                             if (left + modalWidth > window.innerWidth - padding) {
                               left = selectedCountry.clickX - modalWidth - offset;
                             }
                             
                             // If modal would go off the bottom edge, position it above click
                             if (top + modalHeight > window.innerHeight - padding) {
                               top = selectedCountry.clickY - modalHeight - offset;
                             }
                             
                             // Ensure modal stays within viewport bounds
                             if (left < padding) left = padding;
                             if (top < padding) top = padding;
                             
                             return {
                               left: `${left}px`,
                               top: `${top}px`
                             };
                           })()
                         }}>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {countryDetail.countryInfo?.name || 'Unknown'}
                        </h3>
                        <button
                          onClick={closeCountryDetail}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-xl transition-colors duration-200"
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Partner Risk Score Section */}
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Partner Risk Score</h4>
                          
                          {/* Main Risk Score */}
                          <div className="flex justify-between items-center mb-4">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Score</span>
                            <span className="text-2xl font-bold dark:text-white">
                              {countryDetail.countryInfo?.partnerRiskScore?.toFixed(2) || 'N/A'}
                            </span>
                          </div>
                          
                          {/* WGI and FSI Breakdown */}
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">WGI Score (Average)</span>
                              <span className="text-lg font-semibold dark:text-gray-400">
                                {countryDetail.countryInfo?.wgiScore?.toFixed(2) || 'N/A'}
                              </span>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">FSI Score (Average)</span>
                              <span className="text-lg font-semibold dark:text-gray-400">
                                {countryDetail.countryInfo?.fsiScore?.toFixed(2) || 'N/A'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Risk Level Indicator */}
                          <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Risk Level</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                (() => {
                                  const risk = countryDetail.countryInfo?.partnerRiskScore;
                                  if (risk >= 4.5) return 'bg-green-700 text-green-100';      // Very Low Risk
                                  if (risk >= 3.5) return 'bg-green-600 text-green-100';      // Low Risk
                                  if (risk >= 2.5) return 'bg-yellow-600 text-yellow-100';    // Medium Risk
                                  if (risk >= 1.5) return 'bg-red-600 text-red-100';          // High Risk
                                  return 'bg-red-700 text-red-100';                           // Very High Risk
                                })()
                              }`}>
                                {(() => {
                                  const risk = countryDetail.countryInfo?.partnerRiskScore;
                                  if (risk >= 4.5) return 'VERY LOW RISK';
                                  if (risk >= 3.5) return 'LOW RISK';
                                  if (risk >= 2.5) return 'MEDIUM RISK';
                                  if (risk >= 1.5) return 'HIGH RISK';
                                  return 'VERY HIGH RISK';
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Country Situation Details */}
                        {countrySituation && (
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mt-4 transition-colors duration-200">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Country Situation Details</h4>
                            
                            <div className="space-y-3">
                              <div className="flex justify-between items-start">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Country</span>
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">{countrySituation.country}</span>
                              </div>
                              
                              <div className="flex justify-between items-start">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Year</span>
                                <span className="text-sm font-semibold dark:text-white">{countrySituation.year}</span>
                              </div>
                              
                              <div className="mt-3">
                                <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">Situation Description</span>
                                <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed bg-gray-200 dark:bg-gray-800 p-3 rounded-lg transition-colors duration-200">
                                  {countrySituation.description}
                                </div>
                              </div>
                               {/* AI-generated summary (if available) */}
                              {countrySituationAI && countrySituationAI.data && countrySituationAI.data.ai_summary && (
                                <div className="mt-3">
                                  <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">AI Summary</span>
                                  <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed bg-gray-200 dark:bg-gray-800 p-3 rounded-lg transition-colors duration-200">
                                    {countrySituationAI.data.ai_summary}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Policy Details */}
                        {policyDetails && policyDetails.length > 0 && (
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mt-4 transition-colors duration-200">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Policy Details</h4>
                            </div>
                            
                            {/* Policy Names and Links */}
                            <div className="space-y-2">
                              {policyDetails.map((policy, index) => (
                                <div key={index} className="bg-gray-200 dark:bg-gray-800 p-3 rounded-lg transition-colors duration-200">
                                  <div className="flex justify-between items-center">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {policy.policy_title || 'Policy Title Not Available'}
                                    </div>
                                    {policy.reference_url && (
                                      <a 
                                        href={policy.reference_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded transition-colors"
                                      >
                                        View Policy
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Policy Details Loading */}
                        {policyLoading && (
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mt-4 transition-colors duration-200">
                            <div className="flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400"></div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">Loading policy details...</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Loading state for country situation */}
                        {situationLoading && (
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mt-4 transition-colors duration-200">
                            <div className="flex items-center justify-center space-x-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400"></div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">Loading country situation...</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Import Dependency Insights */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Import Dependency Insights</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">Key performance indicators that highlight import trends, supplier concentration, and supply chain risks across critical minerals. These metrics help identify vulnerabilities and opportunities for strategic diversification.</p>
            
            {/* Trade Trends KPIs Cards */}
            {tradeTrendsKPIs && !tradeTrendsLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {/* KPI 1: Fastest-Growing Imported Mineral */}
                {tradeTrendsKPIs.fastest_growing_import && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Fastest-Growing Imported Mineral</div>
                    </div>
                    
                    <div className="kpi-value">
                      {tradeTrendsKPIs.fastest_growing_import.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-green)' }}>
                      +{tradeTrendsKPIs.fastest_growing_import.growth_rate}%
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      {tradeTrendsKPIs.fastest_growing_import.start_year}–{tradeTrendsKPIs.fastest_growing_import.end_year} growth
                    </div>
                  </div>
                )}

                {/* KPI 2: Strategic Dependency Concentration */}
                {tradeTrendsKPIs.strategic_dependency_concentration && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Strategic Dependency Concentration</div>
                    </div>
                    
                    <div className="kpi-value">
                      {tradeTrendsKPIs.strategic_dependency_concentration.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-red)' }}>
                      {tradeTrendsKPIs.strategic_dependency_concentration.cr3}%
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      Top 3 suppliers concentration
                    </div>
                  </div>
                )}

                {/* KPI 3: Import Value Leader */}
                {tradeTrendsKPIs.import_value_leader && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Import Value Leader</div>
                    </div>
                    
                    <div className="kpi-value">
                      {tradeTrendsKPIs.import_value_leader.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-blue)' }}>
                      ${tradeTrendsKPIs.import_value_leader.value_billions}B
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      Highest import bill
                    </div>
                  </div>
                )}

                {/* KPI 4: Import Growth Volatility */}
                {tradeTrendsKPIs.import_growth_volatility && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Import Growth Volatility</div>
                    </div>
                    
                    <div className="kpi-value">
                      {tradeTrendsKPIs.import_growth_volatility.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-orange)' }}>
                      σ = {tradeTrendsKPIs.import_growth_volatility.volatility} ppt
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      Most erratic trends
                    </div>
                  </div>
                )}

                {/* KPI 5: Supply Diversification Score */}
                {tradeTrendsKPIs.supply_diversification_score && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Supply Diversification Score</div>
                    </div>
                    
                    <div className="kpi-value">
                      {tradeTrendsKPIs.supply_diversification_score.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-purple)' }}>
                      {tradeTrendsKPIs.supply_diversification_score.diversification_score.toFixed(2)}
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      HHI-based diversification
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Loading state for trade trends KPIs */}
            {tradeTrendsLoading && (
              <div className="flex justify-center py-8">
                <div className="text-gray-500 dark:text-gray-400">Loading import dependency insights...</div>
              </div>
            )}

            {/* Error state for trade trends KPIs */}
            {!tradeTrendsLoading && tradeTrendsError && (
              <div className="flex justify-center py-8">
                <div className="text-red-500 dark:text-red-400">Error: {tradeTrendsError}</div>
              </div>
            )}

            {/* No data state */}
            {!tradeTrendsLoading && !tradeTrendsError && !tradeTrendsKPIs && (
              <div className="flex justify-center py-8">
                <div className="text-gray-500 dark:text-gray-400">No import dependency insights data available.</div>
              </div>
            )}
          </div>
        </section>

        {/* Section 3: Trade Balance – Treemaps */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Imports, Exports, and Dependency Structure</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This visualization presents trade balance patterns for critical minerals, showing import and export relationships alongside dependency levels. It helps understand trade concentration, competitive positioning, and long-term balance trends. </p>
            <TradeBalanceTreemap />
          </div>
        </section>

        {/* Section 3: Supplier Concentration Treemap */}
        {/* <section className="mb-8">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-600">
            <h2 className="text-3xl font-bold mb-6 text-white">Supplier Concentration Treemap</h2>
            <SupplierConcentrationTreemap selectedMineral={supplierDependenceMineral} />
          </div>
        </section> */}

        {/* Section 4: Strategic Supplier Dependence Tracker */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Concentration and Market Dependence</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This tracker measures supplier concentration using standard indices and diversity metrics. It helps assess overreliance on limited suppliers and informs strategies for improving supply security and diversification. </p>
            
            {/* Supplier Dependence Mineral Selector */}
            <div className="flex items-center flex-wrap gap-4 mb-8">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Mineral:</label>
                <select
                  value={supplierDependenceMineral}
                  onChange={(e) => setSupplierDependenceMineral(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableSupplierMinerals.map(mineral => (
                    <option key={mineral} value={mineral}>{mineral}</option>
                  ))}
                </select>
              </div>
              
            </div>

            <StrategicSupplierDependenceCompatible 
              selectedMineral={supplierDependenceMineral}
            />
          </div>
        </section>

        {/* Section 5: Economic Complexity Index (ECI) Rankings */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Supplier Sophistication and Capability</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This chart compares the economic complexity of countries engaged in critical mineral production and trade. It indicates the relative maturity and diversification of supplier economies, providing insight into supply reliability. </p>
            <ECIBumpChart />
          </div>
        </section>

        {/* Section 6: Strategic Diversification Analysis */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Global Opportunity Mapping</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This analysis identifies potential opportunities for diversifying the supply of critical minerals. It combines multiple indicators to highlight markets that offer balanced, strategic, and sustainable sourcing options. </p>
            <DiversificationStrategicAnalysis 
              selectedMineral={supplierDependenceMineral}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default PartnersRiskPage;
